// eslint-disable-next-line @typescript-eslint/no-unused-vars
import React, {FC, Fragment, useState, useEffect, useLayoutEffect, useMemo, useRef} from "react";
// antd:
import { Layout } from "antd";
// import { LoadingOutlined } from "@ant-design/icons";
// Redux-hooks:
import { useAppSelector, useAppDispatch } from "../libs/redux-hooks.ts";
// Reducers:
import { getLng, getDict, getCompact, getDesktopType, getAllApps, setXMLtoApp, setAppState, setAppJSON, clearAppJSON } from "../libs/reducer.ts";
// Components:
import { LayoutMainToolbar, AppIcons } from "../components/react/index";
// import DivE from "../components/DivE.tsx";

import fnc from "../libs/fnc.ts";
import uigen from "../libs/uigen.tsx";

// Types
import type {Tapp} from "../types/uiapp.d.ts"; // TuiState

const { rz } = window;

const stopDoubleAction = {loadXML: false, loadScripts: false};

const stlBgImgContentNotFound:{[key:string]:string} = {
  backgroundImage: "url('/img/app/bg/page-not-found.png')",
  backgroundColor: "rgba(70,70,70,100)", backgroundBlendMode: "multiply", OBackgroundSize: "cover", MozBackgroundSize: "cover", WebkitBackgroundSize: "cover", backgroundPosition: "center center"
};
const stlBgImgError:{[key:string]:string} = {
  backgroundImage: "url('/img/app/bg/error.png')",
  backgroundColor: "rgba(70,70,70,100)", backgroundBlendMode: "multiply", OBackgroundSize: "cover", MozBackgroundSize: "cover", WebkitBackgroundSize: "cover", backgroundPosition: "center center"
};

const LoadingSpinner = (<div className="uk-flex uk-flex-center uk-flex-middle" style={{height: "100%"}}><div className="loader02"></div></div>);

const NotFoundContent:FC = (msg:string) => (
  <div className="uk-flex uk-flex-center uk-flex-middle" style={{height: "100%"}}>
    <h1 className="uk-margin-remove uk-text-center txt-white txt-stroke-black-shadow">{msg}</h1>
  </div>
);

const ErrorContent:FC = (msg:string) => (
  <div className="uk-flex uk-flex-center uk-flex-middle" style={{height: "100%"}}>
    <h2 className="uk-margin-remove uk-text-center txt-white txt-stroke-red-shadow">{msg}</h2>
  </div>
);

const PageConstructor:FC = () => {
  const dispatch = useAppDispatch();
  const dct = useAppSelector(getDict);
  const lng = useAppSelector(getLng);
  const compact = useAppSelector(getCompact);
  const isMobile = useAppSelector(getDesktopType);
  const rlng = (lng === "en" ? "ru" : "en");
  const allApps = useAppSelector(getAllApps);

  const [docURI, setDocURI] = useState(document.location.pathname);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const [startRender, setStartRender] = useState(false);
  const [finishRender, setFinishRender] = useState(false);
  const [appIcon, setAppIcon] = useState<{icopath:string, attrs:{color?:string, className?:string}}>({icopath: "", attrs: {}});
  const [appTitle, setAppTitle] = useState("");
  const [rootAttributes, setRootAttributes] = useState<{[key:string]: any}>({});

  const [isToolbarVisible, setToolbarVisible] = useState<boolean>(false);

  // Get app params
  const [appindex, app] = useMemo<[number, Tapp | undefined]>(() => {
    const ind = rz.arrFindObj(allApps, {link: document.location.pathname});
    const iap = (~ind ? allApps[ind] : undefined);
    return [ind, iap]
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allApps, document.location.pathname]);

  const [xmlLoaded, setXmlLoaded] = useState(Boolean(app && app.templateXML));
  const [scriptsLoaded, setScriptsLoaded] = useState(false);

  // Parse Query string
  useEffect(() => {
    const qs = rz.urlQueryObject();
    if (qs && (qs.iframe === "true" || qs.hidetoolbar === "true")) { setToolbarVisible(false) }
    else { setToolbarVisible(true) }
  }, []);

  // Debug:
  // useEffect(() => { console.log({docURI, pathName: document.location.pathname}) }, [docURI, document.location.pathname]);

  // ReDraw Desktop-container if User changes Screen-size:
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [dummyValue, setRedrawTimeStamp] = useState(() => ((new Date().getTime())));
  useLayoutEffect(() => { // On document was changed size
    function updateWindowSize() { setRedrawTimeStamp((new Date().getTime())) }
    window.addEventListener("resize", updateWindowSize);
    return () => window.removeEventListener("resize", updateWindowSize);
  }, []);

  // Set app Tiltle and Icon
  useEffect(() => {
    if (!app) { return }
    setAppIcon({icopath: app?.icon || "", attrs: {color: (app?.iconColor ? app.iconColor : undefined), className: app?.iconClass || ""}});
    setAppTitle(app["title_" + lng] || app.title || app["title_" + rlng] || "");
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [app, lng]);

  // If we have a new APPLICATION URI
  useEffect(() => {
    if (docURI !== document.location.pathname) {
      setDocURI(document.location.pathname); setXmlLoaded(false); setScriptsLoaded(false); setStartRender(false); setFinishRender(false); setErrorMessage(""); setLoading(true);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [docURI, document.location.pathname]);

  // Load XML-template
  useEffect(() => {
    if (!app || !loading || xmlLoaded) { return } // || app.templateXML
    if (!stopDoubleAction.loadXML) {
      setStartRender(false); setFinishRender(false); setErrorMessage(""); setLoading(true);

      stopDoubleAction.loadXML = true; setTimeout(() => { stopDoubleAction.loadXML = false }, 1000);
      fnc.loadPage(app).then((ans1) => {
        if (ans1.err) { setErrorMessage(ans1.err); console.log(ans1.err); setLoading(false) }
        else if (ans1.result && ans1.app && ans1.app.templateXML) {
          setXmlLoaded(true);
          dispatch(setXMLtoApp(ans1.app));
          dispatch(clearAppJSON(ans1.app.key));
        }
      });
    }
  }, [app, dispatch, loading, xmlLoaded]);

  // Load scripts
  useEffect(() => {
    if (!app || !loading || !xmlLoaded || scriptsLoaded) { return }
    if (!stopDoubleAction.loadScripts) {
      stopDoubleAction.loadScripts = true; setTimeout(() => { stopDoubleAction.loadScripts = false }, 1000);
      // Dynamically load linked javascripts
      fnc.dynamicallyLoadScripts(app).then((ans2) => {
        // eslint-disable-next-line max-len
        // if (fnc.config?.isDev) rz.log({message: "fnc.dynamicallyLoadScripts(): " + (ans2.scripts || []).join(", "), module: "PageConstructor.tsx", caller: "<PageConstructor> -> useEffect()[app] -> fnc.loadPage(app)"});
        if (ans2.err) { setErrorMessage(ans2.err); console.log(ans2.err); setLoading(false) }
        else { setTimeout(() => { setScriptsLoaded(true); setStartRender(true) }, 500) }
      });
    }
  }, [app, dispatch, loading, xmlLoaded, scriptsLoaded]);

  useEffect(() => {
    if (finishRender || (!startRender || !app || !app.templateXML)) return;
    // =========================================
    // Conver XML-DOM to React-DOM
    const ans1 = uigen.ConvertXMLtoJSON(app.templateXML);

    if (ans1.result) {
      if (ans1.rootAttributes && ans1.rootAttributes.key) {
        setRootAttributes(ans1.rootAttributes);
        if (ans1.elements) {
          // =============== GLOBAL APP JSON ================
          dispatch(setAppJSON({appkey: ans1.rootAttributes.key, newJSON: ans1.elements}));
          // =============== GLOBAL APP STATE ===============
          const newState = uigen.buildUIstate(ans1.elements, ans1.rootAttributes);
          dispatch(setAppState({appkey: ans1.rootAttributes.key, newState}));
          // ================================================
          setFinishRender(true); setLoading(false);
        }
        else { setErrorMessage("<template>...EMPTY OR UNKNOWN ELEMENTS...</template>"); setLoading(false) }
      }
      else { setErrorMessage("Missing 'key' attribute in the root element: <template key='???????'></template>"); setLoading(false) }
    }
    else { setErrorMessage(ans1.err || dct.e.unknowErrorWhileReactElementsBuilding); setLoading(false) }

    // =========================================
  }, [app, startRender, dispatch, finishRender, dct.e.unknowErrorWhileReactElementsBuilding]);

  // Set Page Title
  useEffect(() => {
    if (rootAttributes && rootAttributes.key && app) {
      setAppTitle(rootAttributes["title_" + lng] || rootAttributes.title || rootAttributes["title_" + rlng] || "");
      // eslint-disable-next-line max-len
      setAppIcon({icopath: rootAttributes?.icon || app?.icon || "", attrs: {color: (rootAttributes?.iconColor || app?.iconColor ? rootAttributes?.iconColor || app?.iconColor : undefined), className: rootAttributes?.iconClass || app?.iconClass || ""}});
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rootAttributes]);

  const [toolbarHeight, setToolbarHeight] = useState(isToolbarVisible ? (compact ? 45 : 50) : 0);
  const [dsktpMarginTop, setDsktpMarginTop] = useState(isMobile ? 2 : 5);
  useEffect(() => {
    // const mToolbarHeight = $("#layout-main-toolbar").height(); console.log({mToolbarHeight});
    setToolbarHeight(isToolbarVisible ? (compact ? 45 : 50) : 0);
    setDsktpMarginTop((isToolbarVisible ? (compact ? 45 : 50) : 0) + (isMobile ? 0 : 5));
  }, [isToolbarVisible, compact, isMobile]);

  const pageConstructorContent = useMemo(() => (
    <uigen.GeneratePage rootAttributes={rootAttributes} />
  ), [rootAttributes]);

  const pageSkelet = (elmns:any) => (
    <Layout id="antd-layout" className="layout">
      {/* <div className="global-overlay" style={{display: (!globalMask ? "none" : undefined)}} /> */}
      <div id="background-image" style={(!~appindex) ? (errorMessage ? stlBgImgError : stlBgImgContentNotFound) : {}}>
        {isToolbarVisible && <LayoutMainToolbar pageTitle={appTitle} pageIcon={
          // eslint-disable-next-line max-len
          <AppIcons icopath={appIcon.icopath} attrs={{ color: (appIcon.attrs?.color ? appIcon.attrs.color : undefined), className: appIcon.attrs?.className || "", tooltip: appTitle, size: "1.8em" }} />
        } />}
        <style>
          { // @ts-ignore
            ":root{--desktop-height: " + ((window.innerHeight) - toolbarHeight - 8) + "px;}" // (toolbarRef?.current?.offsetHeight || 40)
          }
        </style>
        <div key="virtual-desktop" id="virtual-desktop" className="uk-container-expand uk-position-relative"
          // @ts-ignore
          style={{height: (window.innerHeight) - toolbarHeight - 8, width: "100%", marginTop: dsktpMarginTop}} // (toolbarRef?.current?.offsetHeight || 40)
        >
          {elmns}
        </div>
      </div>
    </Layout>
  );

  if (!~appindex) return pageSkelet(NotFoundContent(dct.e.pageNotfoundOrDenied));
  if (loading) return pageSkelet(LoadingSpinner);
  if (errorMessage) return pageSkelet(ErrorContent(errorMessage));

  return pageSkelet(
    <Fragment key={rootAttributes?.key || app?.key}>
      {pageConstructorContent}
    </Fragment>
  );
};

export default PageConstructor;
